/**
 * FundsPage — top-level page at /funds with three tabs:
 * - Tab 1: Indices (Nifty 50, Sensex, etc.)
 * - Tab 2: Mutual Funds (research UI with watchlist + analytics)
 * - Tab 3: Global Markets (US / Canadian indices + Commodities)
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import headerLogo from '../assets/branding/Header_logo.png';
import HeaderBackButton from '../components/HeaderBackButton';
import IndicesTab from '../components/funds/IndicesTab';
import MutualFundsTab from '../components/funds/MutualFundsTab';
import GlobalMarketsTab from '../components/funds/GlobalMarketsTab';
import NotificationsBell from '../components/notifications/NotificationsBell';
import '../styles/FundsPage.css';

const TABS = [
    { key: 'indices', label: 'Indices' },
    { key: 'mutual-funds', label: 'Mutual Funds' },
    { key: 'global-markets', label: 'Global Markets' },
];

const FundsPage = () => {
    const navigate = useNavigate();
    const context = useOutletContext() || {};
    const isDarkMode = context.isDarkMode ?? localStorage.getItem('theme') === 'dark';
    const toggleTheme = context.toggleTheme ?? (() => { });
    const handleMenuOpen = context.handleMenuOpen ?? (() => { });
    const getUserInitials = context.getUserInitials ?? (() => 'U');
    const getDisplayName = context.getDisplayName ?? (() => 'User');
    const getProfilePhotoUrl = context.getProfilePhotoUrl ?? (() => '');
    const getProfilePhotoLoading = context.getProfilePhotoLoading ?? (() => false);
    const profilePhotoUrl = getProfilePhotoUrl();
    const profilePhotoLoading = getProfilePhotoLoading();
    const handleAdminOperations = context.handleAdminOperations ?? (() => { });

    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const tabFromUrl = TABS.some((tab) => tab.key === tabParam) ? tabParam : 'indices';
    const [activeTab, setActiveTab] = useState(tabFromUrl);

    useEffect(() => {
        setActiveTab(tabFromUrl);
    }, [tabFromUrl]);

    const selectTab = (key) => {
        setActiveTab(key);
        const next = new URLSearchParams(searchParams);
        if (key === 'indices') next.delete('tab');
        else next.set('tab', key);
        setSearchParams(next, { replace: true });
    };

    return (
        <div className="funds-page">
            {/* Header */}
            <header className="unified-header">
                <div className="header-brand" onClick={() => navigate('/home')}>
                    <img src={headerLogo} alt="Finlytics Forge" className="header-logo-svg" />
                    <span className="brand__text">
                        <span className="brand__title">Finlytics</span>
                        <span className="brand__subtitle">Forge</span>
                    </span>
                </div>
                <HeaderBackButton to="/home" label="Home" />

                <div className="header-tagline">
                    Mutual Funds &amp; Indices
                </div>

                <div className="header-actions">
                    <button
                        className="action-btn"
                        onClick={() => navigate('/dashboard')}
                        title="Stocks Research"
                    >
                        📊
                    </button>
                    <button
                        className="action-btn admin-btn"
                        onClick={handleAdminOperations}
                        title="Admin"
                    >
                        ⚙️
                    </button>
                    <NotificationsBell />
                    <button
                        className="action-btn theme-btn"
                        onClick={toggleTheme}
                        title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
                    >
                        {isDarkMode ? '☀️' : '🌙'}
                    </button>
                    <div className="user-profile" onClick={handleMenuOpen}>
                        <span className="user-name">{getDisplayName()}</span>
                        <div className="user-avatar">
                            {profilePhotoLoading ? (
                                <div className="user-avatar-shimmer" />
                            ) : profilePhotoUrl ? (
                                <img src={profilePhotoUrl} alt={getDisplayName()} className="user-avatar-img" />
                            ) : (
                                getUserInitials()
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Tab bar */}
            <nav className="funds-tabs" role="tablist">
                {TABS.map((tab) => (
                    <button
                        key={tab.key}
                        role="tab"
                        aria-selected={activeTab === tab.key}
                        className={`funds-tab ${activeTab === tab.key ? 'funds-tab--active' : ''}`}
                        onClick={() => selectTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </nav>

            {/* Tab content */}
            <div className="funds-content" role="tabpanel">
                {activeTab === 'indices' && <IndicesTab />}
                {activeTab === 'mutual-funds' && <MutualFundsTab />}
                {activeTab === 'global-markets' && <GlobalMarketsTab />}
            </div>
        </div>
    );
};

export default FundsPage;
